import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useCVContext } from '@/context/CVContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Header from '@/components/Header';
import { ArrowLeft, Download, FileText, Calendar, CheckCircle, AlertCircle, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { downloadFeedbackPdf } from '@/services/pdfService';
import { useDownloadGate } from '@/hooks/useDownloadGate';
import DownloadGateModal from '@/components/DownloadGateModal';
import ScoreCard from '@/components/ScoreCard';
import ScoreCelebration from '@/components/ScoreCelebration';
import { trackEvent } from '@/lib/posthog';

export default function CVFeedback() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { state } = useCVContext();
  const [feedback, setFeedback] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const reportRef = useRef(null);

  const isEnglish = language === 'en';
  const profile = state?.profile;

  // Download gate hook
  const {
    showModal: showDownloadGate,
    initiateDownload,
    onLeadCaptured,
    closeModal: closeDownloadGate,
    downloadType
  } = useDownloadGate(profile?.id);

  useEffect(() => {
    const savedFeedback = localStorage.getItem('cvFeedback');
    if (savedFeedback) {
      try {
        const parsed = JSON.parse(savedFeedback);
        setFeedback(parsed);
        
        // Show celebration if scores are present and not already seen this session
        if (parsed.scoreBefore != null && parsed.scoreAfter != null) {
          const celebrationSeen = sessionStorage.getItem('celebrationSeen_' + parsed.profileId);
          if (!celebrationSeen) {
            setShowCelebration(true);
            sessionStorage.setItem('celebrationSeen_' + parsed.profileId, 'true');
          }
          trackEvent('cv_score_generated', {
            score_before: parsed.scoreBefore,
            score_after: parsed.scoreAfter,
            tips_count: parsed.tipsCount || 0,
            language
          });
        }
      } catch (e) {
        console.error('Error parsing feedback:', e);
        navigate('/');
      }
    } else {
      navigate('/');
    }
  }, [navigate, language]);

  const executeDownloadPDF = async () => {
    if (!feedback) return;
    
    setDownloading(true);
    toast.loading(isEnglish ? 'Generating PDF...' : 'Génération du PDF...', { id: 'pdf-loading' });
    
    try {
      // Use server-side PDF generation
      await downloadFeedbackPdf(feedback, isEnglish ? 'en' : 'fr');
      
      toast.dismiss('pdf-loading');
      toast.success(isEnglish ? 'PDF downloaded!' : 'PDF téléchargé !');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.dismiss('pdf-loading');
      toast.error(isEnglish ? 'Error generating PDF' : 'Erreur lors de la génération');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPDF = async () => {
    await initiateDownload(executeDownloadPDF, 'cv_feedback_pdf');
  };

  // Parse the feedback content to extract sections
  const parseFeedback = (content) => {
    if (!content) return { sections: [] };
    
    // Simple parsing - split by common patterns
    const lines = content.split('\n').filter(line => line.trim());
    const sections = [];
    let currentSection = { title: '', content: [] };
    
    lines.forEach(line => {
      // Check if it's a header (starts with emoji, bold markdown, or specific patterns)
      if (line.match(/^[🎯✨💡🔍📝⚡️🌟]/)) {
        if (currentSection.content.length > 0) {
          sections.push({ ...currentSection });
        }
        currentSection = { title: line, content: [] };
      } else if (line.startsWith('**') || line.startsWith('##')) {
        if (currentSection.content.length > 0) {
          sections.push({ ...currentSection });
        }
        currentSection = { title: line.replace(/\*\*/g, '').replace(/##/g, '').trim(), content: [] };
      } else {
        currentSection.content.push(line);
      }
    });
    
    if (currentSection.content.length > 0 || currentSection.title) {
      sections.push(currentSection);
    }
    
    return { sections };
  };

  if (!feedback) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5BA946]"></div>
      </div>
    );
  }

  const { sections } = parseFeedback(feedback.content);
  const feedbackDate = new Date(feedback.timestamp).toLocaleDateString(isEnglish ? 'en-US' : 'fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Action Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {isEnglish ? 'Back' : 'Retour'}
          </Button>
          
          <Button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="bg-[#5BA946] hover:bg-[#4a9339] text-white flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {isEnglish ? 'Download PDF' : 'Télécharger PDF'}
          </Button>
        </div>

        {/* Report Content */}
        <Card ref={reportRef} className="bg-white shadow-lg rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <FileText className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">
                  {isEnglish ? 'CV Analysis Report' : 'Rapport d\'Analyse CV'}
                </h1>
                <div className="flex items-center gap-2 text-white/80 mt-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">{feedbackDate}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Introduction */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">
                    {isEnglish ? 'Personalized Feedback' : 'Feedback Personnalisé'}
                  </h3>
                  <p className="text-amber-700 text-sm">
                    {isEnglish 
                      ? 'This analysis was generated by our AI assistant based on your uploaded CV. Use these insights to improve your professional profile.'
                      : 'Cette analyse a été générée par notre assistant IA basé sur votre CV téléchargé. Utilisez ces conseils pour améliorer votre profil professionnel.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Feedback Content */}
            <div className="prose prose-gray max-w-none">
              {sections.length > 0 ? (
                sections.map((section, idx) => (
                  <div key={idx} className="mb-6">
                    {section.title && (
                      <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        {section.title.includes('✨') || section.title.includes('Positif') || section.title.includes('Strength') ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : section.title.includes('💡') || section.title.includes('Améliorer') || section.title.includes('Improve') ? (
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                        ) : null}
                        {section.title}
                      </h3>
                    )}
                    <div className="text-gray-600 space-y-2">
                      {section.content.map((line, lineIdx) => (
                        <p key={lineIdx}>{line}</p>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-600 whitespace-pre-wrap leading-relaxed">
                  {feedback.content}
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="bg-gray-50 rounded-xl p-5 text-center">
              <p className="text-gray-600 mb-4">
                {isEnglish 
                  ? 'Ready to apply these improvements to your CV?'
                  : 'Prêt à appliquer ces améliorations à votre CV ?'}
              </p>
              <Button
                onClick={() => navigate('/chat')}
                className="bg-[#5BA946] hover:bg-[#4a9339] text-white"
              >
                {isEnglish ? 'Continue Editing CV' : 'Continuer à Modifier le CV'}
              </Button>
            </div>

            {/* Score Card & Share */}
            {feedback?.scoreBefore !== undefined && feedback?.scoreAfter !== undefined && (
              <div className="rounded-xl p-4">
                <ScoreCard
                  firstName={profile?.first_name || ''}
                  scoreBefore={feedback.scoreBefore}
                  scoreAfter={feedback.scoreAfter}
                  tipsCount={feedback.tipsCount || 0}
                />
              </div>
            )}
          </div>
        </Card>
      </main>

      {/* Score Celebration Modal */}
      {showCelebration && feedback?.scoreBefore != null && feedback?.scoreAfter != null && (
        <ScoreCelebration
          firstName={profile?.first_name || ''}
          scoreBefore={feedback.scoreBefore}
          scoreAfter={feedback.scoreAfter}
          tipsCount={feedback.tipsCount || 0}
          onSeeTips={() => setShowCelebration(false)}
          onClose={() => setShowCelebration(false)}
        />
      )}

      {/* Download Gate Modal */}
      <DownloadGateModal
        isOpen={showDownloadGate}
        onClose={closeDownloadGate}
        onSuccess={onLeadCaptured}
        profileId={profile?.id}
        firstName={profile?.first_name}
        downloadType={downloadType}
        language={isEnglish ? 'en' : 'fr'}
      />
    </div>
  );
}
